import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { invitationsApi, type InvitationPreview } from '../../api/invitations.api';
import { useAuthStore } from '../../stores/auth.store';
import LoadingScreen from '../../components/ui/LoadingScreen';
import Spinner from '../../components/ui/Spinner';
import { Icon, MapPin, CalendarDays, Users, ArrowRight } from '../../components/ui/Icon';
import { toast } from '../../components/ui/Toast';

type ErrorKind =
  | 'INVITATION_NOT_FOUND' | 'INVITATION_REVOKED' | 'INVITATION_EXPIRED' | 'INVITATION_ALREADY_USED'
  | 'INVITATION_YOU_ARE_OWNER' | 'INVITATION_EMAIL_MISMATCH' | 'UNKNOWN';

const errorKeyMap: Record<ErrorKind, { titleKey: string; bodyKey: string }> = {
  INVITATION_NOT_FOUND:     { titleKey: 'invite.errors.invalidTitle',   bodyKey: 'invite.errors.invalidBody' },
  INVITATION_REVOKED:       { titleKey: 'invite.errors.cancelledTitle', bodyKey: 'invite.errors.cancelledBody' },
  INVITATION_EXPIRED:       { titleKey: 'invite.errors.expiredTitle',   bodyKey: 'invite.errors.expiredBody' },
  INVITATION_ALREADY_USED:  { titleKey: 'invite.errors.usedTitle',      bodyKey: 'invite.errors.usedBody' },
  INVITATION_YOU_ARE_OWNER: { titleKey: 'invite.errors.ownTitle',       bodyKey: 'invite.errors.ownBody' },
  // ADR-0033: an agency's invitation is for the email it was sent to.
  INVITATION_EMAIL_MISMATCH: { titleKey: 'invite.errors.emailMismatchTitle', bodyKey: 'invite.errors.emailMismatchBody' },
  UNKNOWN:                  { titleKey: 'invite.errors.failedTitle',    bodyKey: 'invite.errors.failedBody' },
};

function toErrorKind(code: string | undefined): ErrorKind {
  if (!code) return 'UNKNOWN';
  if (code in errorKeyMap) return code as ErrorKind;
  return 'UNKNOWN';
}

export default function AcceptInvitePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuthStore();
  const { t } = useTranslation();
  const token = params.get('token') ?? '';

  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [error, setError]     = useState<ErrorKind | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('INVITATION_NOT_FOUND');
      setLoading(false);
      return;
    }
    invitationsApi.preview(token)
      .then(setPreview)
      .catch((err: unknown) => {
        const code = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
        setError(toErrorKind(code));
      })
      .finally(() => setLoading(false));
  }, [token]);

  // Auto-accept once the preview loads for an authenticated user. Arriving at
  // this URL while signed-in is clear consent — no extra click needed.
  useEffect(() => {
    if (!preview || authLoading || !isAuthenticated || accepting || error) return;
    setAccepting(true);
    invitationsApi.accept(token)
      .then(({ tripId }) => {
        toast(t('invite.joined'), 'success');
        navigate(`/trips/${tripId}/itinerary`, { replace: true });
      })
      .catch((err: unknown) => {
        const code = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
        setError(toErrorKind(code));
        setAccepting(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview, isAuthenticated, authLoading]);

  const authRedirect = `/invite/accept?token=${token}`;
  function goToLogin() {
    navigate(`/login?redirect=${encodeURIComponent(authRedirect)}`);
  }
  function goToRegister() {
    navigate(`/register?token=${encodeURIComponent(token)}&redirect=${encodeURIComponent(authRedirect)}`);
  }

  if (authLoading || loading) return <LoadingScreen fullscreen />;

  if (error) {
    const copy = errorKeyMap[error] ?? errorKeyMap.UNKNOWN;
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#faf7f0' }}>
        <div className="max-w-md px-8 text-center">
          <div className="w-14 h-14 mx-auto mb-5 rounded-2xl bg-slate-100 flex items-center justify-center">
            <span className="text-2xl">✕</span>
          </div>
          <h1 className="font-serif text-3xl font-light leading-tight mb-3" style={{ color: '#0b1420', letterSpacing: -0.8 }}>
            {t(copy.titleKey)}
          </h1>
          <p className="text-sm leading-relaxed mb-6" style={{ color: '#3a4558' }}>
            {t(copy.bodyKey)}
          </p>
          <button
            onClick={() => navigate(isAuthenticated ? '/dashboard' : '/login')}
            className="font-serif font-medium text-base px-6 py-3 rounded-xl transition-colors"
            style={{ background: '#0b1420', color: '#faf7f0' }}
          >
            {isAuthenticated ? t('invite.goDashboard') : t('invite.goHome')}
          </button>
        </div>
      </div>
    );
  }

  if (!preview) return null;

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#faf7f0' }}>
      <div className="w-full max-w-md px-8">
        <p className="font-mono text-[10px] tracking-[2px] uppercase mb-3" style={{ color: '#8a5a2b' }}>
          {t('invite.eyebrow')}
        </p>
        <h1 className="font-serif text-[42px] font-light leading-none tracking-tight mb-4" style={{ color: '#0b1420', letterSpacing: -1.6 }}>
          {t('invite.inviterText', { inviterName: preview.inviterName })}
        </h1>
        <p className="font-serif font-medium text-lg leading-relaxed mb-8" style={{ color: '#3a4558' }}>
          {t('invite.toCollaborate', { tripName: preview.tripName })}
        </p>

        <div className="rounded-2xl p-5 mb-7 space-y-3" style={{ background: '#fff', border: '1px solid #d6dae2' }}>
          <div className="flex items-center gap-3 text-sm" style={{ color: '#0b1420' }}>
            <Icon as={MapPin} size={16} className="text-slate-400" />
            <span>{preview.tripDestination}</span>
          </div>
          <div className="flex items-center gap-3 text-sm" style={{ color: '#0b1420' }}>
            <Icon as={CalendarDays} size={16} className="text-slate-400" />
            <span>{preview.tripDates}</span>
          </div>
          <div className="flex items-center gap-3 text-sm" style={{ color: '#0b1420' }}>
            <Icon as={Users} size={16} className="text-slate-400" />
            <span>Rol: <strong className="font-semibold">{preview.role === 'EDITOR' ? t('invite.roleEditor') : t('invite.roleViewer')}</strong></span>
          </div>
        </div>

        {isAuthenticated ? (
          <button
            disabled
            className="w-full flex items-center justify-center gap-3 font-serif font-medium text-lg"
            style={{ height: 56, background: '#0b1420', color: '#faf7f0', borderRadius: 10, border: 'none', cursor: 'wait', opacity: 0.7 }}
          >
            {accepting ? <Spinner size="sm" /> : t('invite.joining')}
          </button>
        ) : (
          <>
            <p className="text-[13px] leading-relaxed mb-4 text-center" style={{ color: '#3a4558' }}>
              {t('invite.needAccount')}
            </p>

            <button
              onClick={goToRegister}
              className="w-full flex items-center justify-center gap-3 font-serif font-medium text-lg transition-all mb-3"
              style={{ height: 56, background: '#0b1420', color: '#faf7f0', borderRadius: 10, border: 'none', cursor: 'pointer' }}
            >
              {t('invite.createAndJoin')}
              <span className="w-7 h-7 rounded-full flex items-center justify-center not-italic" style={{ background: '#d9b382', color: '#0b1420' }}>
                <Icon as={ArrowRight} size={13} />
              </span>
            </button>

            <button
              onClick={goToLogin}
              className="w-full flex items-center justify-center gap-2 text-sm font-medium transition-colors"
              style={{ height: 48, background: 'transparent', color: '#0b1420', borderRadius: 10, border: '1px solid #d6dae2', cursor: 'pointer' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#0b1420'; e.currentTarget.style.background = '#f4f1ea'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#d6dae2'; e.currentTarget.style.background = 'transparent'; }}
            >
              {t('invite.haveAccount')}
            </button>
          </>
        )}

        <p className="font-mono text-[10px] tracking-[1.4px] uppercase mt-6 text-center" style={{ color: '#6b7689' }}>
          {t('invite.expiresOn', { date: new Date(preview.expiresAt).toLocaleDateString() })}
        </p>
      </div>
    </div>
  );
}
